import { defineStore } from 'pinia';
import { useMapLayerStore } from '@/stores/mapLayer.store';
import { ensureOwnerAnalysisLayers } from '@/composables/gtfs/map/registerAnalysisLayerCatalog.js';
import { registerInteractionLayers } from '@/composables/gtfs/map/bootstrapSharedMapLayers.js';
import { clearLegacyAnalysisMapState } from '@/composables/gtfs/clearLegacyAnalysisMapState.js';
import { applyViewModeVisibility } from '@/composables/gtfs/map/useAnalysisViewModeSync.js';
/** Nav sourceKey → owner */
export const NAV_TO_OWNER = {
    isochrone: 'isochrone',
    corridor_sync: 'corridor',
    service_availability: 'service-availability',
    transit_desert: 'transit-desert',
};
export const ANALYSIS_NAV_KEYS = Object.keys(NAV_TO_OWNER);
/** Her owner'ın desteklediği görüntüleme modları */
export const OWNER_VIEW_MODES = {
    isochrone: ['detail', 'coverage', 'administrative'],
    catchment: ['detail', 'coverage', 'administrative'],
    'service-availability': ['detail', 'coverage', 'administrative'],
    corridor: ['detail'],
    'transit-desert': ['administrative'],
};
const restoreRegistry = new Map();
/** owner → layerId → son yazılan analiz kaynağı (pasif sahip önbelleği) */
const ownerSourceCache = new Map();
const capturedLayerStores = new WeakSet();
/**
 * Analiz sahibi geri dönünce sonucu haritaya yeniden basan kayıt.
 * Dönen fonksiyon kaydı kaldırır.
 */
export function registerAnalysisRestore(ownerId, fn) {
    restoreRegistry.set(ownerId, fn);
    return () => unregisterAnalysisRestore(ownerId, fn);
}
export function unregisterAnalysisRestore(ownerId, fn) {
    if (fn && restoreRegistry.get(ownerId) !== fn)
        return;
    restoreRegistry.delete(ownerId);
}
export function hasAnalysisRestore(ownerId) {
    return restoreRegistry.has(ownerId);
}
export function rememberAnalysisSource(ownerId, layerId, data) {
    if (!ownerId || ownerId === 'shared' || !layerId)
        return;
    let layers = ownerSourceCache.get(ownerId);
    if (!layers) {
        layers = new Map();
        ownerSourceCache.set(ownerId, layers);
    }
    layers.set(layerId, data);
}
export function getCachedAnalysisSource(ownerId, layerId) {
    return ownerSourceCache.get(ownerId)?.get(layerId) ?? null;
}
export function clearAnalysisSourceCache(ownerId) {
    if (ownerId)
        ownerSourceCache.delete(ownerId);
    else
        ownerSourceCache.clear();
}
export function isAnalysisOwnerActive(ownerId) {
    try {
        return useAnalysisWorkspaceStore().activeOwnerId === ownerId;
    }
    catch {
        return false;
    }
}
function captureLayerSources(layerStore) {
    if (capturedLayerStores.has(layerStore))
        return;
    capturedLayerStores.add(layerStore);
    layerStore.$onAction(({ name, args }) => {
        if (name !== 'setSourceData')
            return;
        const [category, ownerId, layerId, data] = args;
        if (category !== 'analysis')
            return;
        rememberAnalysisSource(ownerId, layerId, data);
    }, true);
}
function replayOwnerSources(layerStore, ownerId) {
    const layers = ownerSourceCache.get(ownerId);
    if (!layers)
        return;
    for (const [layerId, data] of layers) {
        layerStore.setSourceData('analysis', ownerId, layerId, data);
    }
}
function runOwnerRestore(ownerId) {
    const fn = restoreRegistry.get(ownerId);
    if (!fn)
        return;
    try {
        fn();
    }
    catch (e) {
        console.warn(`Analiz geri yükleme başarısız (${ownerId}):`, e);
    }
}
export const ZOOM_DISTRICT_MAX = 11;
export const ZOOM_NEIGHBORHOOD_MIN = 11;
export const ZOOM_DETAIL_MIN = 13;
export const ZOOM_CLUSTER_MIN = 11;
/**
 * Analiz workspace: aktif owner, view mode, idari seçim, fly-to.
 * Layer Manager ile birlikte analiz geçişini yönetir.
 */
export const useAnalysisWorkspaceStore = defineStore('analysisWorkspace', {
    state: () => ({
        activeOwnerId: null,
        activeNavKey: null,
        /** Kullanıcı seçimi; null = otomatik (zoom) */
        viewMode: 'coverage',
        viewModeLocked: false,
        mapZoom: 11,
        selectedNeighborhoodId: null,
        selectedDistrictId: null,
        hoverFeatureId: null,
        hoverLevel: null,
        hoverLabel: '',
        hoverMeta: null,
        /** Sonuçlar listesi / harita ortak fly-to isteği */
        flyToRequest: null,
        /** Transit Desert ölçüt: coverage | severity */
        desertMetric: 'severity',
        /** KPI / sonuç özeti — aktif analiz doldurur */
        resultsSummary: null,
        selectedUnitStats: null,
        /** Base sınırlar yüklendi mi */
        baseBoundariesReady: false,
        /** Sahip başına son görünüm; geri dönünce aynı görünüm açılır */
        ownerViewState: {},
        /** Geri yükleme sinyali — harita bileşeni yeniden çizim için izleyebilir */
        restoreRequest: null,
    }),
    getters: {
        supportedViewModes(state) {
            if (!state.activeOwnerId)
                return ['coverage'];
            return OWNER_VIEW_MODES[state.activeOwnerId] || ['coverage'];
        },
        /**
         * Kullanıcı kilidi varsa viewMode; yoksa zoom'a göre detail/coverage.
         * Administrative her zaman sabit.
         */
        effectiveViewMode(state) {
            const supported = OWNER_VIEW_MODES[state.activeOwnerId || ''] || ['coverage'];
            if (state.viewModeLocked || state.viewMode === 'administrative') {
                return supported.includes(state.viewMode)
                    ? state.viewMode
                    : supported[0];
            }
            // Otomatik: yalnız detail ↔ coverage
            if (!supported.includes('coverage') && !supported.includes('detail')) {
                return supported[0];
            }
            if (state.mapZoom >= ZOOM_DETAIL_MIN && supported.includes('detail')) {
                return 'detail';
            }
            if (supported.includes('coverage'))
                return 'coverage';
            return supported[0];
        },
        /** Zooma göre hangi idari seviye tıklanabilir */
        selectableAdminLevel(state) {
            if (state.mapZoom < ZOOM_NEIGHBORHOOD_MIN)
                return 'ilce';
            return 'mahalle';
        },
        hasSelection(state) {
            return !!(state.selectedNeighborhoodId || state.selectedDistrictId);
        },
    },
    actions: {
        setMapZoom(z) {
            this.mapZoom = Number(z) || 0;
        },
        setViewMode(mode, { lock = true } = {}) {
            const supported = this.supportedViewModes;
            if (!supported.includes(mode))
                return;
            this.viewMode = mode;
            this.viewModeLocked = !!lock;
        },
        unlockViewMode() {
            this.viewModeLocked = false;
        },
        setDesertMetric(metric) {
            this.desertMetric = metric;
        },
        setResultsSummary(summary) {
            this.resultsSummary = summary;
        },
        setSelectedUnitStats(stats) {
            this.selectedUnitStats = stats;
        },
        /** Shell açılışında: analiz kaynak yazımlarını sahip önbelleğine al */
        bindLayerSourceCapture() {
            captureLayerSources(useMapLayerStore());
        },
        /**
         * Nav değişimi — atomik geçiş.
         * Analysis + Interaction haritadan kaldırılır; Base/Reference korunur.
         * Pasif sahibin verisi önbellekte kalır; yeni sahip restore() ile geri basılır.
         */
        switchAnalysis(navKey) {
            const layerStore = useMapLayerStore();
            captureLayerSources(layerStore);
            const nextOwner = navKey ? NAV_TO_OWNER[navKey] || null : null;
            const prevOwner = this.activeOwnerId;
            if (prevOwner === nextOwner && this.activeNavKey === navKey) {
                return;
            }
            if (prevOwner) {
                this.ownerViewState = {
                    ...this.ownerViewState,
                    [prevOwner]: {
                        viewMode: this.viewMode,
                        viewModeLocked: this.viewModeLocked,
                        desertMetric: this.desertMetric,
                        resultsSummary: this.resultsSummary,
                    },
                };
            }
            layerStore.onAnalysisChange();
            registerInteractionLayers();
            clearLegacyAnalysisMapState();
            this.clearSelection({ keepHover: false });
            this.resultsSummary = null;
            this.selectedUnitStats = null;
            this.flyToRequest = null;
            this.activeNavKey = navKey;
            this.activeOwnerId = nextOwner;
            if (nextOwner) {
                ensureOwnerAnalysisLayers(nextOwner);
            }
            const saved = nextOwner ? this.ownerViewState[nextOwner] : null;
            const modes = this.supportedViewModes;
            if (saved && modes.includes(saved.viewMode)) {
                this.viewMode = saved.viewMode;
                this.viewModeLocked = saved.viewModeLocked;
                this.desertMetric = saved.desertMetric;
                this.resultsSummary = saved.resultsSummary;
            }
            else {
                // Varsayılan: izokron/catchment → detail; SA → buffer (detail); desert → administrative
                let preferred = modes[0];
                if (nextOwner === 'isochrone' || nextOwner === 'catchment') {
                    preferred = modes.includes('detail') ? 'detail' : modes[0];
                }
                else if (nextOwner === 'service-availability') {
                    preferred = modes.includes('detail') ? 'detail' : modes[0];
                }
                else if (modes.includes('coverage')) {
                    preferred = 'coverage';
                }
                else if (modes.includes('administrative')) {
                    preferred = 'administrative';
                }
                this.viewMode = preferred;
                this.viewModeLocked =
                    nextOwner === 'isochrone' || nextOwner === 'service-availability';
                this.desertMetric = 'severity';
            }
            if (nextOwner) {
                replayOwnerSources(layerStore, nextOwner);
                runOwnerRestore(nextOwner);
                applyViewModeVisibility(nextOwner, this.effectiveViewMode);
                this.restoreRequest = {
                    ownerId: nextOwner,
                    seq: (this.restoreRequest?.seq || 0) + 1,
                };
            }
        },
        /**
         * Harita veya panel listesinden ortak seçim.
         */
        selectAdminUnit(payload) {
            if (!payload?.featureKey || !payload.level)
                return;
            if (payload.level === 'mahalle') {
                this.selectedNeighborhoodId = payload.featureKey;
                this.selectedDistrictId = null;
            }
            else if (payload.level === 'ilce') {
                this.selectedDistrictId = payload.featureKey;
                this.selectedNeighborhoodId = null;
            }
            this.selectedUnitStats = payload.stats || null;
            if (payload.flyTo !== false) {
                this.requestFlyTo({
                    featureKey: payload.featureKey,
                    level: payload.level,
                    bbox: payload.bbox,
                    center: payload.center,
                });
            }
        },
        clearSelection({ keepHover = true } = {}) {
            this.selectedNeighborhoodId = null;
            this.selectedDistrictId = null;
            this.selectedUnitStats = null;
            if (!keepHover) {
                this.hoverFeatureId = null;
                this.hoverLevel = null;
                this.hoverLabel = '';
                this.hoverMeta = null;
            }
            const layerStore = useMapLayerStore();
            // Seçim vurgusunu temizle ama interaction iskeletini koru
            layerStore.setSourceData('interaction', 'shared', 'selected-feature', { type: 'FeatureCollection', features: [] });
            layerStore.setSourceData('interaction', 'shared', 'hover-feature', { type: 'FeatureCollection', features: [] });
        },
        setHover(payload) {
            if (!payload?.featureKey) {
                this.hoverFeatureId = null;
                this.hoverLevel = null;
                this.hoverLabel = '';
                this.hoverMeta = null;
                return;
            }
            this.hoverFeatureId = payload.featureKey;
            this.hoverLevel = payload.level || null;
            this.hoverLabel = payload.label || '';
            this.hoverMeta = payload.meta || null;
        },
        requestFlyTo(payload) {
            this.flyToRequest = {
                ...payload,
                seq: (this.flyToRequest?.seq || 0) + 1,
            };
        },
        markBaseBoundariesReady(ready = true) {
            this.baseBoundariesReady = !!ready;
        },
    },
});
